import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, UserPlus } from "lucide-react";
import { recruitersAdminApi } from "../api";
import { money, RECRUITER_STATUS } from "../format";
import { Badge, Empty, Tabs, type TabDef } from "../ui";
import type { RecruiterSummary } from "../types";
import { RegisterRecruiterModal } from "./RegisterRecruiterModal";
import { ProposalsPane } from "./ProposalsPane";
import { StatementsPane } from "./StatementsPane";
import { SettingsPane } from "./SettingsPane";
import { LoadingBlock, ErrorBlock } from "../../dashboard/shared/StateBlocks";
import { Can } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

type Tab = "reclutadores" | "propuestas" | "liquidaciones" | "configuracion";

/**
 * Nexus → Reclutadores (lado Mixcore): quiénes son, cómo va su cartera y su
 * checklist, qué hay que revisar y cuánto se les debe. Pestañas por `?tab=`
 * (los correos al equipo enlazan a `?tab=propuestas` y `?tab=liquidaciones`).
 */
export function RecruitersScreen() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "reclutadores";
  const [showRegister, setShowRegister] = useState(false);
  const query = useQuery({ queryKey: ["recruiters", "list"], queryFn: recruitersAdminApi.list });

  const tabs: TabDef<Tab>[] = [
    { id: "reclutadores", label: "Reclutadores", count: query.data?.recruiters.length },
    { id: "propuestas", label: "Propuestas", count: query.data?.proposals },
    { id: "liquidaciones", label: "Liquidaciones" },
    { id: "configuracion", label: "Configuración" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="nx-h1">Reclutadores</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-2xl">
            Programa de Reclutadores Comerciales: cartera, checklist de cada evento y comisiones. Cada reclutador entra a
            Nexus y sólo ve lo suyo.
          </p>
        </div>
        <Can permission={PERMISSIONS.RECRUITERS_MANAGE}>
          <button type="button" onClick={() => setShowRegister(true)} className="nx-btn-primary shrink-0">
            <UserPlus size={14} />
            Registrar reclutador
          </button>
        </Can>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={(t) => setParams(t === "reclutadores" ? {} : { tab: t })} />

      {tab === "reclutadores" && (
        <>
          {query.isLoading && <LoadingBlock label="Cargando reclutadores…" />}
          {query.isError && <ErrorBlock onRetry={() => query.refetch()} />}
          {query.data && <RecruitersList rows={query.data.recruiters} />}
        </>
      )}
      {tab === "propuestas" && <ProposalsPane />}
      {tab === "liquidaciones" && <StatementsPane />}
      {tab === "configuracion" && <SettingsPane />}

      {showRegister && <RegisterRecruiterModal onClose={() => setShowRegister(false)} />}
    </div>
  );
}

function RecruitersList({ rows }: { rows: RecruiterSummary[] }) {
  const navigate = useNavigate();
  if (rows.length === 0) {
    return <Empty>Aún no hay reclutadores. Regístralos con "Registrar reclutador": reciben un correo para entrar a Nexus.</Empty>;
  }
  return (
    <ul className="grid gap-3">
      {rows.map((r) => {
        const st = RECRUITER_STATUS[r.status];
        const owed = r.earnings.payable + r.earnings.inStatement;
        const alerts = r.events.toReview + r.events.observed + r.producers.expiringSoon + r.statements.toPay;
        return (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => navigate(`/reclutadores/${r.id}`)}
              className="nx-card-interactive w-full text-left p-4 grid gap-3 sm:grid-cols-[minmax(0,1.3fr)_repeat(3,minmax(0,1fr))_auto] sm:items-center"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-fg truncate">{r.name}</span>
                  <Badge tone={st.tone}>{st.label}</Badge>
                  {!r.userLinked && <Badge tone="warning">Sin ingresar</Badge>}
                </div>
                <div className="text-xs text-fg-subtle truncate mt-0.5">{r.email}</div>
              </div>
              <Metric label="Cartera" value={`${r.producers.active} activos`} note={`${r.producers.pipeline} en pipeline${r.producers.proposed ? ` · ${r.producers.proposed} propuestos` : ""}`} />
              <Metric
                label="Checklist del mes"
                value={r.events.thisMonth ? `${r.events.checklistsComplete}/${r.events.thisMonth}` : "Sin eventos"}
                note={`${r.events.upcoming} próximos`}
              />
              <Metric label="Por pagar" value={money(owed)} note={`Pagado ${money(r.earnings.paid)}`} />
              <div className="flex items-center justify-between sm:justify-end gap-2">
                {alerts > 0 ? (
                  <span className="inline-flex items-center gap-1 text-xs text-warning">
                    <AlertTriangle size={14} />
                    {[
                      r.events.toReview && `${r.events.toReview} por revisar`,
                      r.events.observed && `${r.events.observed} observados`,
                      r.producers.expiringSoon && `${r.producers.expiringSoon} por caducar`,
                      r.statements.toPay && `${r.statements.toPay} boleta${r.statements.toPay > 1 ? "s" : ""} por pagar`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                ) : (
                  <span className="text-xs text-fg-subtle">Al día</span>
                )}
                <ChevronRight size={16} className="text-fg-subtle shrink-0" />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Metric({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-fg-muted">{label}</div>
      <div className="text-sm font-semibold text-fg tabular-nums truncate">{value}</div>
      {note && <div className="text-xs text-fg-subtle truncate">{note}</div>}
    </div>
  );
}
